import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { parseAsStringLiteral, useQueryState } from "nuqs";
import { supabase } from "@/integrations/supabase/client";
import { useAdminStatsGeneration } from "./useAdminStatsGeneration";

export type Timeframe = "1d" | "1w" | "1m" | "3m" | "1y" | "all";

const TIMEFRAME_OPTIONS = ["1d", "1w", "1m", "3m", "1y", "all"] as const;

const TIMEFRAME_LABELS: Record<Timeframe, string> = {
  "1d": "24h",
  "1w": "7 days",
  "1m": "30 days",
  "3m": "3 months",
  "1y": "1 year",
  "all": "All time",
};

export const TIMEFRAMES: { value: Timeframe; label: string }[] = [
  { value: "1d", label: "1D" },
  { value: "1w", label: "1W" },
  { value: "1m", label: "1M" },
  { value: "3m", label: "3M" },
  { value: "1y", label: "1Y" },
  { value: "all", label: "All" },
];

interface PartnerCodesStats {
  totalCodes: number;
  activeCodes: number;
  inactiveCodes: number;
  creditsCodes: number;
  percentageCodes: number;
  totalRedemptions: number;
  totalCreditsGranted: number;
  uniqueUsersRedeemed: number;
   conversionRate: number;
   avgEventsPerUser: number;
   usersWhoBookedEvents: number;
   totalEventsBookedByRedeemedUsers: number;
}

interface ChartDataPoint {
  name: string;
  redemptions?: number;
  value?: number;
  fill?: string;
}

 interface TopCodeByEngagement {
   name: string;
   eventsBooked: number;
   conversionRate: number;
 }
 
interface PartnerCodesChartData {
  redemptionsOverTime: ChartDataPoint[];
  codesByType: ChartDataPoint[];
  topPartners: ChartDataPoint[];
   topCodesByEngagement: TopCodeByEngagement[];
}

const STALE_THRESHOLD_MS = 60 * 1000; // 1 minute
const STORAGE_KEY = "admin-partner-codes-stats-timeframe";

const getInitialTimeframe = (): Timeframe => {
  if (typeof window === "undefined") return "1m";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && TIMEFRAME_OPTIONS.includes(stored as Timeframe)) {
    return stored as Timeframe;
  }
  return "1m";
};

export const usePartnerCodesStatsCache = () => {
  const [urlTimeframe, setUrlTimeframe] = useQueryState(
    "partnerCodesTf",
    parseAsStringLiteral(TIMEFRAME_OPTIONS).withDefault(getInitialTimeframe())
  );
  
  const timeframe = urlTimeframe as Timeframe;
  const { generateStatsBackground, isGenerating } = useAdminStatsGeneration();
  const hasTriggeredRefresh = useRef(false);

  // Persist to localStorage when timeframe changes
  const setTimeframe = useCallback((newTimeframe: Timeframe) => {
    localStorage.setItem(STORAGE_KEY, newTimeframe);
    setUrlTimeframe(newTimeframe);
  }, [setUrlTimeframe]);

  const { data: cachedData, isLoading } = useQuery({
    queryKey: ["partner-codes-stats-cache", timeframe],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_codes_stats_cache")
        .select("stats, chart_data, generated_at")
        .eq("timeframe", timeframe)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    placeholderData: keepPreviousData,
  });

  // Auto-refresh if data is stale (older than 1 minute)
  useEffect(() => {
    if (!cachedData?.generated_at || isGenerating("partner-codes" as any) || hasTriggeredRefresh.current) {
      return;
    }

    const generatedTime = new Date(cachedData.generated_at).getTime();
    const now = Date.now();
    const isStale = now - generatedTime > STALE_THRESHOLD_MS;

    if (isStale) {
      hasTriggeredRefresh.current = true;
      generateStatsBackground("partner-codes" as any);
    }
  }, [cachedData?.generated_at, isGenerating, generateStatsBackground]);

  // Reset refresh trigger when timeframe changes
  useEffect(() => {
    hasTriggeredRefresh.current = false;
  }, [timeframe]);

  const rawChartData = cachedData?.chart_data as unknown as Partial<PartnerCodesChartData> | undefined;
  
  return {
    timeframe,
    setTimeframe,
    stats: cachedData?.stats as unknown as PartnerCodesStats | null,
    chartData: { 
      redemptionsOverTime: rawChartData?.redemptionsOverTime || [], 
      codesByType: rawChartData?.codesByType || [], 
     topPartners: rawChartData?.topPartners || [],
     topCodesByEngagement: rawChartData?.topCodesByEngagement || [],
    },
    generatedAt: cachedData?.generated_at,
    isLoading,
    isRefreshing: isGenerating("partner-codes" as any),
    timeframeLabel: TIMEFRAME_LABELS[timeframe],
  };
};
